"use client";

import React from "react";
import { ShieldCheck, Lock, LoaderCircle } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="rounded-full bg-green-50 p-4 dark:bg-green-900/20">
          <ShieldCheck className="h-12 w-12 text-green-600 dark:text-green-400" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif font-medium text-foreground">
            Request Received
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Start New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Priya Sharma" {...field} />
              </FormControl>
              <FormDescription>Enter your first and last name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="e.g., priya@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required",
        pattern: {
          value: /^\+?[0-9\s-]{10,}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="e.g., +91 98765 43210" {...field} />
              </FormControl>
              <FormDescription>
                Include country code if outside India.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="service_type" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Consultation Service</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Pre-Matrimonial Due Diligence">
                    Pre-Matrimonial Due Diligence
                  </SelectItem>
                  <SelectItem value="Post-Matrimonial Clarity Support">
                    Post-Matrimonial Clarity Support
                  </SelectItem>
                  <SelectItem value="Private Consultation">
                    Private Consultation
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="brief_message" render={({
        field
      }) => <FormItem>
              <FormLabel>Brief Message (Optional)</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us a little about your situation..." className="min-h-[120px] resize-y" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-red-50 border border-red-100 rounded-md text-red-600 text-sm dark:bg-red-900/20 dark:border-red-900/50 dark:text-red-400">
          {submitError}
        </div>}

      <Button type="submit" className="w-full h-12 text-base font-medium" disabled={isSubmitting}>
        {isSubmitting ? <>
            <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
            Securing Your Request...
          </> : "Schedule Private Consultation"}
      </Button>

      <p className="text-xs text-center text-muted-foreground mt-4">
        By submitting this form, you agree to our strict confidentiality
        protocols.
      </p>
    </Form>;
}
export default function BeginYourConsultation() {
  return <section id="begin-your-consultation" className="py-20 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-secondary/30 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl opacity-50" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto">
          {}
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-8 bg-secondary/50 py-2 px-4 rounded-full w-fit mx-auto border border-border/50">
            <Lock className="h-3.5 w-3.5" />
            <span>100% End-to-End Encrypted & Confidential</span>
          </div>

          <div className="text-center mb-10 space-y-4">
            <h2 className="text-3xl md:text-4xl font-serif text-foreground tracking-tight">
              Begin Your Consultation
            </h2>
            <p className="text-muted-foreground text-lg max-w-lg mx-auto leading-relaxed">
              Take the first step towards clarity. Your privacy is our absolute
              priority.
            </p>
          </div>

          <Card className="border-border/60 shadow-sm bg-card/50 backdrop-blur-sm">
            <CardHeader className="sr-only">
              <CardTitle>Consultation Form</CardTitle>
              <CardDescription>
                Fill out the form below to request a consultation.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 md:p-10">
              <FormProvider formId="consultation_form_get_clarity" sectionName="begin-your-consultation" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              service_type: "",
              brief_message: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              service_type: {
                required: true
              },
              brief_message: {
                required: false
              }
            }} submitText="Schedule Private Consultation" successMessage="Thank you. Your request has been securely received. A senior consultant will contact you within 24 hours via your preferred method." errorMessage="We encountered an issue securing your request. Please try again or contact us directly.">
                <ConsultationForm />
              </FormProvider>
            </CardContent>
          </Card>

          <div className="mt-12 text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              Prefer to speak directly?
            </p>
            <p className="text-foreground font-medium">+91 9718285422</p>
            <p className="text-xs text-muted-foreground">
              Available Mon-Sat, 9am - 6pm IST
            </p>
          </div>
        </div>
      </div>
    </section>;
}